/**
 * Company: SPHMedia
 * Description: [Custom React Hook: usePortraitVideos]
 */

import fetchTMApi from "@helper/fetchTMApi";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type usePortraitVideosData = {
  playing: LatestVideoResponse | undefined;
  videos: LatestVideoResponse[];
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  handleOnEndedVideo: () => void;
};

type usePortraitVideosProps = {
  parentRef?: React.RefObject<HTMLDivElement | null>;
  limit?: number;
};

/**
 * Custom React Hook: usePortraitVideosHook
 * Description: [Custom hook for managing portrait videos]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function usePortraitVideos({
  parentRef,
  limit = 10,
}: usePortraitVideosProps): usePortraitVideosData {
  const [playlistVideos, setPlaylistVideos] = useState<LatestVideoResponse[]>();
  const [videos, setVideos] = useState<LatestVideoResponse[]>();
  const [playing, setPlaying] = useState<LatestVideoResponse | undefined>();
  const hasInitialized = useRef(false);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setVideos(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setVideos],
  );

  const handleOnClickThumbnail = useCallback(
    (props: LatestVideoResponse) => {
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },

    [handleNowPlaying, setPlaying],
  );

  const handleOnEndedVideo = useCallback(() => {
    if (!playlistVideos) return;

    const playlistLength = playlistVideos.length;
    if (playlistLength === 0) return;

    let index: number = playlistVideos.findIndex(
      (video) => video.id === playing?.id,
    );

    index++; // Move to the next video in the playlist
    handleNowPlaying(playlistVideos[index % playlistLength].id);
    setPlaying(playlistVideos[index % playlistLength]);
  }, [handleNowPlaying, playlistVideos, playing]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const videos: LatestVideoResponse[] = await fetchTMApi<
          LatestVideoResponse[]
        >(`latest-portrait-videos?size=${limit}`, "GET");

        setPlaylistVideos(videos);
        setVideos(videos);
      } catch (error) {
        console.error("Something went wrong", error);
      }
    };

    void fetchData();
  }, [limit]);

  useEffect(() => {
    const element = parentRef?.current;

    if (!element || !playlistVideos?.length || hasInitialized.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.25 &&
          !hasInitialized.current
        ) {
          hasInitialized.current = true;

          setPlaying(playlistVideos[0]);
          handleNowPlaying(playlistVideos[0].id);

          observer.disconnect();
        }
      },
      {
        threshold: [0.25],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [parentRef, playlistVideos, handleNowPlaying]);

  return {
    playing,
    videos: videos || [],
    handleOnClickThumbnail,
    handleOnEndedVideo,
  };
}
